import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '@/utils/apiUrl';
import { Search, Loader2, Users, FolderOpen, FileText, FolderKanban, Mail, Paperclip, Sparkles } from 'lucide-react';
import { EmailThreadDialog } from './EmailThreadDialog';

const Section = ({ icon: Icon, title, children }) => (
  <div className="py-1">
    <div className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
      <Icon className="h-3.5 w-3.5" /> {title}
    </div>
    {children}
  </div>
);

export const GlobalSearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [threadEmailId, setThreadEmailId] = useState(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const runSearch = async () => {
    if (query.trim().length < 2) return;
    setLoading(true);
    setOpen(true);
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(`${getApiUrl()}/crm/search?q=${encodeURIComponent(query.trim())}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResults(await res.json());
    } catch (e) {
      console.error('Recherche globale:', e);
      setResults({ total: 0 });
    } finally {
      setLoading(false);
    }
  };

  const go = (path) => { setOpen(false); navigate(path); };
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-CA') : '');

  return (
    <div className="relative flex-1 max-w-lg" ref={boxRef}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
      <input
        type="text"
        value={query}
        placeholder="Rechercher client, dossier, document, courriel..."
        className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
        data-testid="global-search-input"
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') runSearch();
          if (e.key === 'Escape') setOpen(false);
        }}
        onFocus={() => { if (results) setOpen(true); }}
      />
      {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-red-500" />}

      {open && results && (
        <div
          className="absolute left-0 right-0 mt-2 bg-white rounded-xl border border-gray-200 shadow-2xl max-h-[70vh] overflow-y-auto z-50 divide-y divide-gray-100"
          data-testid="global-search-results"
        >
          {results.total === 0 && (
            <div className="px-4 py-6 text-sm text-gray-500 text-center" data-testid="search-no-results">
              Aucun résultat pour « {query} »
            </div>
          )}

          {results.contacts?.length > 0 && (
            <Section icon={Users} title={`Contacts (${results.contacts.length})`}>
              {results.contacts.map((c) => (
                <button key={c.id} onClick={() => go(`/clients-list?q=${encodeURIComponent(c.name || '')}`)}
                  className="w-full text-left px-3 py-2 hover:bg-red-50 transition-colors"
                  data-testid={`search-result-contact-${c.id}`}>
                  <div className="text-sm font-medium text-gray-900">{c.name}</div>
                  <div className="text-xs text-gray-500">{c.email} · {c.role}</div>
                </button>
              ))}
            </Section>
          )}

          {results.cases?.length > 0 && (
            <Section icon={FolderOpen} title={`Dossiers (${results.cases.length})`}>
              {results.cases.map((f) => (
                <button key={f.id} onClick={() => go(`/files/${f.id}`)}
                  className="w-full text-left px-3 py-2 hover:bg-red-50 transition-colors"
                  data-testid={`search-result-case-${f.id}`}>
                  <div className="text-sm font-medium text-gray-900">{f.file_number} — {f.client_name}</div>
                  <div className="text-xs text-gray-500">{f.status}</div>
                </button>
              ))}
            </Section>
          )}

          {results.groups?.length > 0 && (
            <Section icon={FolderKanban} title={`Groupes (${results.groups.length})`}>
              {results.groups.map((g) => (
                <button key={g.id} onClick={() => go(`/groupes/${g.id}`)}
                  className="w-full text-left px-3 py-2 hover:bg-red-50 transition-colors"
                  data-testid={`search-result-group-${g.id}`}>
                  <div className="text-sm font-medium text-gray-900">{g.name}</div>
                  <div className="text-xs text-gray-500">{(g.members || []).join(', ')}</div>
                </button>
              ))}
            </Section>
          )}

          {results.documents?.length > 0 && (
            <Section icon={FileText} title={`Documents (${results.documents.length})`}>
              {results.documents.map((d) => (
                <button key={d.id} onClick={() => d.file_id ? go(`/files/${d.file_id}`) : null}
                  className="w-full text-left px-3 py-2 hover:bg-red-50 transition-colors"
                  data-testid={`search-result-document-${d.id}`}>
                  <div className="text-sm font-medium text-gray-900">{d.name}</div>
                  <div className="text-xs text-gray-500">{d.category || 'document'}</div>
                </button>
              ))}
            </Section>
          )}

          {results.emails?.length > 0 && (
            <Section icon={Mail} title={`Courriels (${results.emails.length})`}>
              {results.emails.map((e) => (
                <button key={e.id} onClick={() => { setOpen(false); setThreadEmailId(e.id); }}
                  className="w-full text-left px-3 py-2 hover:bg-red-50 transition-colors group"
                  data-testid={`search-result-email-${e.id}`}>
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium text-gray-900 truncate flex-1">{e.subject || '(sans objet)'}</div>
                    <span className="flex items-center gap-1 text-[10px] text-violet-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <Sparkles className="h-3 w-3" /> Résumé IA du fil
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {e.from_name || e.from_addr} · {fmtDate(e.date)}
                    {e.candidate_client_name ? ` · ${e.candidate_client_name}` : ''}
                  </div>
                  {e.snippet && <div className="text-xs text-gray-400 truncate italic">{e.snippet}</div>}
                  {e.attachments?.length > 0 && (
                    <div className="flex items-center gap-1 text-xs text-red-400 truncate">
                      <Paperclip className="h-3 w-3 shrink-0" /> {e.attachments.join(', ')}
                    </div>
                  )}
                </button>
              ))}
            </Section>
          )}
        </div>
      )}
      <EmailThreadDialog emailId={threadEmailId} open={!!threadEmailId} onOpenChange={(o) => { if (!o) setThreadEmailId(null); }} />
    </div>
  );
};

export default GlobalSearch;
